import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { and, eq, isUndefined, useLiveQuery } from "@tanstack/react-db";
import { memo, useMemo } from "react";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import {
  NewConversationModal,
  type ExistingDraftData,
} from "./new-conversation-modal";

/**
 * Renders the NewConversationModal pre-filled with data from an existing DRAFT conversation.
 * Used by SupportTicketView when the conversation status is "DRAFT".
 */
export const DraftConversationModal = memo(function DraftConversationModal({
  conversation,
  thread,
  onClose,
  onCreated,
}: {
  conversation: SupportTicketListItemResponse;
  thread: ChannelThread;
  onClose: () => void;
  onCreated: (supportTicketId: string) => void;
}) {
  const collections = useGlobalSupportTicketCollections();
  const draftResult = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          and(
            eq(draft.supportTicketId, String(conversation.id)),
            eq(draft.threadId, String(thread.id)),
            isUndefined(draft.inReplyToMessageId),
            eq(draft.kind, "merchant"),
          ),
        )
        .findOne(),
    [conversation.id, thread.id],
  );
  const threadDraft = draftResult.data ?? null;

  const subject =
    thread.integrationType === "gmail" || thread.integrationType === "outlook"
      ? (thread.channelData?.subject ?? undefined)
      : undefined;

  const existingDraft = useMemo<ExistingDraftData>(
    () => ({
      supportTicketId: conversation.id,
      threadId: thread.id,
      integrationId: thread.integrationId,
      customerEmail: conversation.customer?.email ?? undefined,
      cc:
        threadDraft?.channelData?.cc
          ?.map((r: { email: string }) => r.email)
          .join(", ") || undefined,
      bcc:
        threadDraft?.channelData?.bcc
          ?.map((r: { email: string }) => r.email)
          .join(", ") || undefined,
      subject: subject ?? undefined,
      draftContent: threadDraft?.content ?? undefined,
    }),
    [
      conversation.id,
      conversation.customer?.email,
      thread.id,
      thread.integrationId,
      subject,
      threadDraft?.content,
      threadDraft?.channelData,
    ],
  );

  return (
    <NewConversationModal
      existingDraft={existingDraft}
      onCreated={onCreated}
      open
      setOpen={(open) => {
        if (!open) {
          onClose();
        }
      }}
    />
  );
});
